import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

// Async thunks for notification settings
export const fetchNotificationPreferences = createAsyncThunk(
  'notificationSettings/fetchPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/users/notifications/preferences');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch notification preferences');
    }
  }
);

export const updateNotificationChannels = createAsyncThunk(
  'notificationSettings/updateChannels',
  async (channelSettings, { rejectWithValue }) => {
    try {
      const response = await api.put('/api/users/notifications/channels', channelSettings);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update notification channels');
    }
  }
);

export const updateNotificationSubscriptions = createAsyncThunk(
  'notificationSettings/updateSubscriptions',
  async (subscriptions, { rejectWithValue }) => {
    try {
      const response = await api.put('/api/users/notifications/subscriptions', subscriptions);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update notification subscriptions');
    }
  }
);

const notificationSettingsSlice = createSlice({
  name: 'notificationSettings',
  initialState: {
    channels: {},
    subscriptions: {},
    availableChannels: [],
    availableTypes: [],
    loading: false,
    error: null
  },
  reducers: {
    resetNotificationSettings: (state) => {
      state.channels = {};
      state.subscriptions = {};
      state.availableChannels = [];
      state.availableTypes = [];
      state.loading = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch preferences
      .addCase(fetchNotificationPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotificationPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.channels = action.payload.channels;
        state.subscriptions = action.payload.subscriptions;
        state.availableChannels = action.payload.available_channels;
        state.availableTypes = action.payload.available_types.map(type => ({
          id: type.id,
          name: type.name,
          description: type.description
        }));
      })
      .addCase(fetchNotificationPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update channels
      .addCase(updateNotificationChannels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateNotificationChannels.fulfilled, (state, action) => {
        state.loading = false;
        state.channels = action.payload.channels;
        if (action.payload.subscriptions) {
          state.subscriptions = action.payload.subscriptions;
        }
      })
      .addCase(updateNotificationChannels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update subscriptions
      .addCase(updateNotificationSubscriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateNotificationSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.subscriptions) {
          state.subscriptions = action.payload.subscriptions;
        }
      })
      .addCase(updateNotificationSubscriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { resetNotificationSettings } = notificationSettingsSlice.actions;
export default notificationSettingsSlice.reducer; 